import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  fetchCustomerMessages,
  updateCustomerMessageStatus,
  getCustomerMessageThread,
  addAdminReply,
  type CustomerMessageStatus,
} from '../services/customerMessages.api';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

const STATUS_FILTERS: { value: string | undefined; labelAr: string; labelEn: string }[] = [
  { value: undefined, labelAr: 'الكل', labelEn: 'All' },
  { value: 'open', labelAr: 'مفتوحة', labelEn: 'Open' },
  { value: 'in_progress', labelAr: 'قيد المعالجة', labelEn: 'In Progress' },
  { value: 'resolved', labelAr: 'تم الحل', labelEn: 'Resolved' },
];

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

export function CustomerMessagesPage() {
  const { i18n } = useTranslation();
  const isAr = i18n.language.startsWith('ar');
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});
  const [sendingReplyFor, setSendingReplyFor] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'customer-messages', statusFilter],
    queryFn: () => fetchCustomerMessages(statusFilter),
  });

  const handleStatusChange = async (id: string, status: CustomerMessageStatus) => {
    await updateCustomerMessageStatus(id, status);
    await queryClient.invalidateQueries({ queryKey: ['admin', 'customer-messages'] });
  };

  const { data: threadData } = useQuery({
    queryKey: ['admin', 'customer-messages', 'thread', expandedId],
    queryFn: () => getCustomerMessageThread(expandedId as string),
    enabled: Boolean(expandedId),
  });

  const handleSendReply = async (id: string) => {
    const body = (replyDraft[id] ?? '').trim();
    if (!body) return;
    setSendingReplyFor(id);
    try {
      await addAdminReply(id, body);
      setReplyDraft((prev) => ({ ...prev, [id]: '' }));
      await queryClient.invalidateQueries({ queryKey: ['admin', 'customer-messages'] });
    } finally {
      setSendingReplyFor(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">
          {isAr ? 'شكاوى ورسائل العملاء' : 'Customer Messages'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isAr
            ? 'الرسائل الواردة من عملاء جميع المنتجات (وارد، ينبوع، وغيرها)'
            : 'Incoming messages from customers across all products'}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value ?? 'all'}
            type="button"
            className={cn(
              'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
              statusFilter === filter.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-muted/60',
            )}
            onClick={() => setStatusFilter(filter.value)}
          >
            {isAr ? filter.labelAr : filter.labelEn}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{isAr ? 'جارِ التحميل...' : 'Loading...'}</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {isAr ? 'حدث خطأ أثناء تحميل الرسائل' : 'Failed to load messages'}
        </p>
      ) : !data?.items.length ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center">
          <Icons.Eye className="mx-auto h-8 w-8 text-muted-foreground/50" aria-hidden />
          <p className="mt-3 text-sm text-muted-foreground">
            {isAr ? 'لا توجد رسائل حاليًا' : 'No messages yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((msg) => (
            <div key={msg.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground">
                      {msg.product_name}
                    </span>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground">{msg.tenant_name}</span>
                  </div>
                  <h3 className="mt-1 font-semibold text-foreground">{msg.subject}</h3>
                  <p className="text-xs text-muted-foreground">
                    {msg.created_by_name ?? msg.created_by_email} · {msg.created_by_email}
                  </p>
                </div>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2.5 py-1 text-xs font-medium',
                    STATUS_STYLES[msg.status] ?? 'bg-muted text-muted-foreground',
                  )}
                >
                  {msg.status}
                </span>
              </div>

              <p
                className={cn(
                  'mt-3 text-sm text-foreground',
                  expandedId !== msg.id && 'line-clamp-2',
                )}
              >
                {msg.body}
              </p>

              {msg.body.length > 120 ? (
                <button
                  type="button"
                  className="mt-1 text-xs font-medium text-primary hover:underline"
                  onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                >
                  {expandedId === msg.id ? (isAr ? 'عرض أقل' : 'Show less') : isAr ? 'عرض المزيد' : 'Show more'}
                </button>
              ) : null}

              {msg.attachments?.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {msg.attachments.map((att, idx) => (
                    <a
                    
                      key={idx}
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border border-border px-2 py-1 text-xs text-primary hover:bg-muted/60"
                    >
                      {att.fileName}
                    </a>
                  ))}
                </div>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                {(['open', 'in_progress', 'resolved'] as CustomerMessageStatus[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={msg.status === s}
                    className={cn(
                      'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40',
                      'border-border text-muted-foreground hover:bg-muted/60',
                    )}
                    onClick={() => void handleStatusChange(msg.id, s)}
                  >
                    {s}
                  </button>
                ))}
                <button
                  type="button"
                  className="rounded-lg border border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
                  onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                >
                  {isAr ? 'المحادثة والرد' : 'Thread & Reply'}
                </button>
              </div>

              {expandedId === msg.id ? (
                <div className="mt-4 space-y-3 rounded-xl border border-border bg-muted/30 p-3">
                  {threadData?.replies.length ? (
                    <div className="space-y-2">
                      {threadData.replies.map((r) => (
                        <div
                          key={r.id}
                          className={cn(
                            'rounded-lg p-2 text-sm',
                            r.author_type === 'admin'
                              ? 'bg-primary/10 text-foreground'
                              : 'bg-background text-foreground',
                          )}
                        >
                          <div className="mb-1 text-xs font-medium text-muted-foreground">
                            {r.author_type === 'admin' ? (isAr ? 'الدعم الفني' : 'Support') : r.author_name ?? (isAr ? 'العميل' : 'Customer')}
                            {' · '}
                            {new Date(r.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-US')}
                          </div>
                          {r.body}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {isAr ? 'لا توجد ردود بعد' : 'No replies yet'}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <textarea
                      className="flex-1 rounded-lg border border-border bg-background p-2 text-sm"
                      rows={2}
                      placeholder={isAr ? 'اكتب ردك هنا...' : 'Write your reply...'}
                      value={replyDraft[msg.id] ?? ''}
                      onChange={(e) =>
                        setReplyDraft((prev) => ({ ...prev, [msg.id]: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      disabled={sendingReplyFor === msg.id || !(replyDraft[msg.id] ?? '').trim()}
                      className="shrink-0 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-40"
                      onClick={() => void handleSendReply(msg.id)}
                    >
                      {sendingReplyFor === msg.id ? (isAr ? 'جارِ الإرسال...' : 'Sending...') : isAr ? 'إرسال' : 'Send'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
